import { useState, useEffect } from 'react';
import { Target } from 'lucide-react';
import { Button } from './Button';
import { createGoalOnly } from '../lib/ladderUtils';
import { supabase } from '../lib/supabase';

interface CreateGoalProps {
  userId: string;
  onGoalCreated: (goalId?: string) => void;
  onCreateLadder?: (goalId: string) => void;
  onCancel?: () => void;
}

export function CreateGoal({ userId, onGoalCreated, onCreateLadder, onCancel }: CreateGoalProps) {
  const [goalName, setGoalName] = useState('');
  const [alignment, setAlignment] = useState('');
  const [finalOutcome, setFinalOutcome] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [coreValues, setCoreValues] = useState<string[]>([]);

  useEffect(() => {
    const fetchCoreValues = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('core_value_1, core_value_2, core_value_3')
          .eq('id', userId)
          .single();

        if (profile) {
          const values = [
            profile.core_value_1,
            profile.core_value_2,
            profile.core_value_3
          ].filter(Boolean);
          setCoreValues(values);
        }
      } catch (error) {
        console.error('Error fetching core values:', error);
      }
    };

    fetchCoreValues();
  }, [userId]);

  const handleCreateGoal = async () => {
    setError('');

    if (!goalName.trim()) {
      setError('Please enter a goal name');
      return;
    }

    if (!alignment.trim()) {
      setError('Please describe how this goal aligns with your values');
      return;
    }

    if (!finalOutcome.trim()) {
      setError('Please write your "I am" statement');
      return;
    }

    setIsSubmitting(true);

    try {
      const goal = await createGoalOnly(
        userId,
        goalName.trim(),
        alignment.trim(),
        finalOutcome.trim()
      );

      if (goal) {
        onGoalCreated();
      } else {
        setError('Failed to create goal. Please try again.');
      }
    } catch (err) {
      console.error('Error creating goal:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAndGoToLadder = async () => {
    setError('');

    if (!goalName.trim()) {
      setError('Please enter a goal name');
      return;
    }

    if (!alignment.trim()) {
      setError('Please describe how this goal aligns with your values');
      return;
    }

    if (!finalOutcome.trim()) {
      setError('Please write your "I am" statement');
      return;
    }

    setIsSubmitting(true);

    try {
      const goal = await createGoalOnly(
        userId,
        goalName.trim(),
        alignment.trim(),
        finalOutcome.trim()
      );

      if (goal && onCreateLadder) {
        onCreateLadder(goal.id);
      } else {
        setError('Failed to create goal. Please try again.');
      }
    } catch (err) {
      console.error('Error creating goal:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <Target className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-semibold text-gray-900">Create New Goal</h2>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="goalName" className="block text-sm font-medium text-gray-700 mb-2">
            Goal Name (Identity Level)
          </label>
          <input
            type="text"
            id="goalName"
            value={goalName}
            onChange={(e) => setGoalName(e.target.value)}
            placeholder="e.g., Become a confident public speaker"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="alignment" className="block text-sm font-medium text-gray-700 mb-2">
            Alignment
          </label>
          <textarea
            id="alignment"
            value={alignment}
            onChange={(e) => setAlignment(e.target.value)}
            placeholder={
              coreValues.length > 0
                ? `How does this goal reflect and reinforce your values of ${coreValues.join(', ')}?`
                : 'How does this goal reflect and reinforce your core values?'
            }
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="finalOutcome" className="block text-sm font-medium text-gray-700 mb-2">
            Declare
          </label>
          <textarea
            id="finalOutcome"
            value={finalOutcome}
            onChange={(e) => setFinalOutcome(e.target.value)}
            placeholder='Write an "I am" statement (e.g. I am a confident speaker that inspires others to take action).'
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleCreateGoal}
            disabled={isSubmitting}
            variant="outline"
          >
            {isSubmitting ? 'Creating...' : 'Create Goal'}
          </Button>

          {onCreateLadder && (
            <Button
              onClick={handleCreateAndGoToLadder}
              disabled={isSubmitting}
              variant="outline"
            >
              {isSubmitting ? 'Creating...' : 'Create Goal and Go to Ladder'}
            </Button>
          )}

          {onCancel && (
            <Button
              onClick={onCancel}
              variant="outline"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
